/**
 * Vendor Management Tab
 * 
 * Handles vendor/supplier management and purchase orders
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, Plus, Trash2, Edit, RefreshCw, Search,
  Phone, Mail, Globe, ChevronDown, ChevronUp, Loader2,
  Package, FileText, Check, X, Star, Clock, DollarSign,
  Truck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Format currency with $ for admin
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

const PAYMENT_TERMS = [
  { id: 'cod', name: 'Cash on Delivery' },
  { id: 'net_7', name: 'Net 7 Days' },
  { id: 'net_14', name: 'Net 14 Days' },
  { id: 'net_30', name: 'Net 30 Days' },
  { id: 'net_60', name: 'Net 60 Days' },
];

const ORDER_STATUSES = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  confirmed: { label: 'Confirmed', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  partial: { label: 'Partial', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  received: { label: 'Received', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

export default function VendorManagementTab() {
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState({});
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [activeTab, setActiveTab] = useState('vendors');
  
  // Vendor form state
  const [vendorForm, setVendorForm] = useState({
    name: '',
    company_name: '',
    category: 'food_supplier',
    phone: '',
    email: '',
    address: '',
    city: '',
    payment_terms: 'net_30',
    credit_limit: '',
    is_preferred: false,
    notes: '',
    supplies: ''
  });
  
  // Order form state
  const [orderForm, setOrderForm] = useState({
    vendor_id: '',
    items: [{ name: '', quantity: 1, unit: 'units', unit_price: 0 }],
    notes: '',
    expected_delivery: ''
  });
  
  // Fetch vendors
  const fetchVendors = useCallback(async () => {
    try {
      let params = 'limit=100';
      if (selectedCategory !== 'all') {
        params += `&category=${selectedCategory}`;
      }
      if (searchQuery) {
        params += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const res = await api.get(`/admin/finance/vendors?${params}`);
      setVendors(res.data.vendors || []);
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
    }
  }, [selectedCategory, searchQuery]);
  
  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/vendors/categories');
      setCategories(res.data.categories || {});
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);
  
  // Fetch orders
  const fetchOrders = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/vendors/orders?limit=50');
      setOrders(res.data.orders || []);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    }
  }, []);
  
  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/vendors/analytics/summary?period=month');
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    fetchCategories();
    fetchVendors();
    fetchOrders();
    fetchAnalytics();
  }, [fetchCategories, fetchVendors, fetchOrders, fetchAnalytics]);
  
  // Refresh when filters change
  useEffect(() => {
    fetchVendors();
  }, [selectedCategory, searchQuery, fetchVendors]);
  
  // Save vendor
  const handleSaveVendor = async () => {
    if (!vendorForm.name) {
      toast.error('Vendor name is required');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...vendorForm,
        credit_limit: vendorForm.credit_limit ? parseFloat(vendorForm.credit_limit) : null,
        supplies: vendorForm.supplies ? vendorForm.supplies.split(',').map(s => s.trim()) : []
      };
      
      if (editingVendor) {
        await api.put(`/admin/finance/vendors/${editingVendor.id}`, payload);
      } else {
        await api.post('/admin/finance/vendors', payload);
      }
      
      toast.success(editingVendor ? 'Vendor updated' : 'Vendor created');
      setShowVendorDialog(false);
      setEditingVendor(null);
      resetVendorForm();
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save vendor');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete vendor
  const handleDeleteVendor = async (id) => {
    if (!confirm('Deactivate this vendor?')) return;
    
    try {
      await api.delete(`/admin/finance/vendors/${id}`);
      toast.success('Vendor deactivated');
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };
  
  // Create purchase order
  const handleCreateOrder = async () => {
    if (!orderForm.vendor_id || orderForm.items.length === 0) {
      toast.error('Select vendor and add items');
      return;
    }
    
    const validItems = orderForm.items.filter(i => i.name && i.quantity > 0);
    if (validItems.length === 0) {
      toast.error('Add at least one valid item');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/admin/finance/vendors/orders', {
        vendor_id: orderForm.vendor_id,
        items: validItems,
        notes: orderForm.notes,
        expected_delivery: orderForm.expected_delivery || null
      });
      
      toast.success('Purchase order created');
      setShowOrderDialog(false);
      resetOrderForm();
      fetchOrders();
      fetchAnalytics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };
  
  // Update order status
  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/finance/vendors/orders/${orderId}/status?status=${status}`);
      toast.success('Order status updated');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };
  
  // Reset forms
  const resetVendorForm = () => {
    setVendorForm({
      name: '', company_name: '', category: 'food_supplier', phone: '', email: '',
      address: '', city: '', payment_terms: 'net_30', credit_limit: '', is_preferred: false,
      notes: '', supplies: ''
    });
  };
  
  const resetOrderForm = () => {
    setOrderForm({
      vendor_id: '', items: [{ name: '', quantity: 1, unit: 'units', unit_price: 0 }],
      notes: '', expected_delivery: ''
    });
  };
  
  // Edit vendor
  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setVendorForm({
      name: vendor.name || '',
      company_name: vendor.company_name || '',
      category: vendor.category || 'food_supplier',
      phone: vendor.phone || '',
      email: vendor.email || '',
      address: vendor.address || '',
      city: vendor.city || '',
      payment_terms: vendor.payment_terms || 'net_30',
      credit_limit: vendor.credit_limit?.toString() || '',
      is_preferred: vendor.is_preferred || false,
      notes: vendor.notes || '',
      supplies: vendor.supplies?.join(', ') || ''
    });
    setShowVendorDialog(true);
  };
  
  // Add order item
  const addOrderItem = () => {
    setOrderForm({
      ...orderForm,
      items: [...orderForm.items, { name: '', quantity: 1, unit: 'units', unit_price: 0 }]
    });
  };
  
  // Update order item
  const updateOrderItem = (index, field, value) => {
    const newItems = [...orderForm.items];
    newItems[index][field] = value;
    setOrderForm({ ...orderForm, items: newItems });
  };
  
  // Remove order item
  const removeOrderItem = (index) => {
    setOrderForm({
      ...orderForm,
      items: orderForm.items.filter((_, i) => i !== index)
    });
  };
  
  // Calculate order total
  const orderTotal = orderForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  
  return (
    <div className="space-y-6" data-testid="vendor-management-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Vendor Management</h2>
          <p className="text-muted-foreground">
            Manage suppliers and purchase orders
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchVendors(); fetchOrders(); fetchAnalytics(); }}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setShowOrderDialog(true)} variant="secondary">
            <FileText className="w-4 h-4 mr-2" />
            New Order
          </Button>
          <Button onClick={() => { resetVendorForm(); setEditingVendor(null); setShowVendorDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Vendor
          </Button>
        </div>
      </div>
      
      {/* Summary Cards */}
      {analytics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Vendors</p>
                  <p className="text-xl font-bold text-foreground">{vendors.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Package className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending Orders</p>
                  <p className="text-xl font-bold text-foreground">{analytics.pending_orders || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Unpaid Balance</p>
                  <p className="text-xl font-bold text-foreground">{formatCurrency(analytics.unpaid_total)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">This Month</p>
                  <p className="text-xl font-bold text-foreground">
                    {formatCurrency(Object.values(analytics.spending_by_category || {}).reduce((s, c) => s + c.total, 0))}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="vendors">
            <Building2 className="w-4 h-4 mr-2" />
            Vendors
          </TabsTrigger>
          <TabsTrigger value="orders">
            <FileText className="w-4 h-4 mr-2" />
            Purchase Orders
          </TabsTrigger>
        </TabsList>
        
        {/* Vendors Tab */}
        <TabsContent value="vendors" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search vendors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(categories).map(([id, cat]) => (
                  <SelectItem key={id} value={id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Vendor List */}
          {vendors.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No vendors found</p>
                <Button className="mt-4" onClick={() => { resetVendorForm(); setShowVendorDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Vendor
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {vendors.map(vendor => (
                <Card key={vendor.id}>
                  <div 
                    className="p-4 cursor-pointer"
                    onClick={() => setExpandedVendor(expandedVendor === vendor.id ? null : vendor.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Building2 className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-foreground">{vendor.name}</p>
                            {vendor.is_preferred && (
                              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {categories[vendor.category]?.name || vendor.category}
                            {vendor.company_name && ` • ${vendor.company_name}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right hidden sm:block">
                          <p className="text-sm text-muted-foreground">Total Spent</p>
                          <p className="font-medium text-foreground">{formatCurrency(vendor.total_spent)}</p>
                        </div>
                        {expandedVendor === vendor.id ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <AnimatePresence>
                    {expandedVendor === vendor.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="px-4 pb-4 border-t pt-4 space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                            {vendor.phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-muted-foreground" />
                                <span>{vendor.phone}</span>
                              </div>
                            )}
                            {vendor.email && (
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-muted-foreground" />
                                <span>{vendor.email}</span>
                              </div>
                            )}
                            {vendor.website && (
                              <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-muted-foreground" />
                                <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                  Website
                                </a>
                              </div>
                            )}
                          </div>
                          
                          {vendor.address && (
                            <p className="text-sm text-muted-foreground">
                              {vendor.address}{vendor.city && `, ${vendor.city}`}
                            </p>
                          )}
                          
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline">
                              {PAYMENT_TERMS.find(t => t.id === vendor.payment_terms)?.name || vendor.payment_terms}
                            </Badge>
                            <Badge variant="outline">{vendor.total_orders || 0} orders</Badge>
                            {vendor.supplies?.length > 0 && (
                              <Badge variant="secondary">{vendor.supplies.length} items supplied</Badge>
                            )}
                          </div>
                          
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleEditVendor(vendor); }}>
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                              onClick={(e) => { e.stopPropagation(); handleDeleteVendor(vendor.id); }}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Deactivate
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          {orders.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No purchase orders yet</p>
                <Button className="mt-4" onClick={() => setShowOrderDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Order
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {orders.map(order => (
                <Card key={order.id}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-foreground">{order.po_number}</p>
                          <Badge className={ORDER_STATUSES[order.status]?.color}>
                            {ORDER_STATUSES[order.status]?.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {order.vendor_name} • {order.items?.length || 0} items
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Created: {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-lg font-bold text-foreground">{formatCurrency(order.total)}</p>
                          <p className={`text-xs ${order.payment_status === 'paid' ? 'text-green-600' : 'text-amber-600'}`}>
                            {order.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                          </p>
                        </div>
                        
                        <Select 
                          value={order.status} 
                          onValueChange={(v) => handleUpdateOrderStatus(order.id, v)}
                        >
                          <SelectTrigger className="w-[130px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(ORDER_STATUSES).map(([id, s]) => (
                              <SelectItem key={id} value={id}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Add/Edit Vendor Dialog */}
      <Dialog open={showVendorDialog} onOpenChange={(open) => {
        setShowVendorDialog(open);
        if (!open) { setEditingVendor(null); resetVendorForm(); }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
            <DialogDescription>Enter vendor details</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vendor Name *</Label>
                <Input
                  value={vendorForm.name}
                  onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })}
                  placeholder="Contact name"
                />
              </div>
              <div>
                <Label>Company Name</Label>
                <Input
                  value={vendorForm.company_name}
                  onChange={(e) => setVendorForm({ ...vendorForm, company_name: e.target.value })}
                  placeholder="Business name"
                />
              </div>
            </div>
            
            <div>
              <Label>Category *</Label>
              <Select value={vendorForm.category} onValueChange={(v) => setVendorForm({ ...vendorForm, category: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(categories).map(([id, cat]) => (
                    <SelectItem key={id} value={id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={vendorForm.phone}
                  onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })}
                  placeholder="+679..."
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={vendorForm.email}
                  onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Address</Label>
              <Input
                value={vendorForm.address}
                onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={vendorForm.city}
                  onChange={(e) => setVendorForm({ ...vendorForm, city: e.target.value })}
                />
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Select value={vendorForm.payment_terms} onValueChange={(v) => setVendorForm({ ...vendorForm, payment_terms: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TERMS.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Items Supplied (comma-separated)</Label>
              <Input
                value={vendorForm.supplies}
                onChange={(e) => setVendorForm({ ...vendorForm, supplies: e.target.value })}
                placeholder="Coffee beans, milk, sugar..."
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={vendorForm.notes}
                onChange={(e) => setVendorForm({ ...vendorForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={vendorForm.is_preferred}
                onChange={(e) => setVendorForm({ ...vendorForm, is_preferred: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm">Preferred vendor</span>
              <Star className="w-4 h-4 text-amber-500" />
            </label>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendorDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveVendor} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {editingVendor ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Order Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Purchase Order</DialogTitle>
            <DialogDescription>Add items to order from a vendor</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Vendor *</Label>
              <Select value={orderForm.vendor_id} onValueChange={(v) => setOrderForm({ ...orderForm, vendor_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor..." />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="mb-2 block">Order Items</Label>
              <div className="space-y-2">
                {orderForm.items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Input
                        placeholder="Item name"
                        value={item.name}
                        onChange={(e) => updateOrderItem(idx, 'name', e.target.value)}
                      />
                    </div>
                    <div className="w-20">
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateOrderItem(idx, 'quantity', parseInt(e.target.value) || 0)}
                      />
                    </div>
                    <div className="w-24">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Price"
                        value={item.unit_price || ''}
                        onChange={(e) => updateOrderItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="text-red-600"
                      onClick={() => removeOrderItem(idx)}
                      disabled={orderForm.items.length === 1}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" className="mt-2" onClick={addOrderItem}>
                <Plus className="w-4 h-4 mr-1" />
                Add Item
              </Button>
            </div>
            
            <div className="bg-muted/50 p-3 rounded-lg">
              <div className="flex justify-between text-lg font-bold">
                <span>Total:</span>
                <span>{formatCurrency(orderTotal)}</span>
              </div>
            </div>
            
            <div>
              <Label>Expected Delivery</Label>
              <Input
                type="date"
                value={orderForm.expected_delivery}
                onChange={(e) => setOrderForm({ ...orderForm, expected_delivery: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={orderForm.notes}
                onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateOrder} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Truck className="w-4 h-4 mr-2" />}
              Create Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
